class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    // Wait for the next frame to ensure DOM is fully ready
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    this.container = this.querySelector('.swiper-container');
    if (!this.container) return;

    const autoplayAttr = this.getAttribute('data-autoplay') === 'true';

    const config = {
      slidesPerView: 1,
      loop: true,
      // If using swiper-bundle.min.js, these will work. 
      // If using modules, make sure you've imported EffectFade and Autoplay.
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      autoplay: autoplayAttr ? {
        delay: 5000,
        disableOnInteraction: false,
      } : false,
      speed: 800,
      grabCursor: true
    };

    const mountSwiper = () => {
      if (typeof Swiper !== 'undefined') {
        this.swiper = new Swiper(this.container, config);
        this.dataset.initialized = "true"; // Useful for CSS transitions
      } else {
        console.error('Swiper is not defined. Make sure swiper-bundle.min.js is loaded.');
      }
    };

    // Check if Swiper is already there, otherwise wait for it
    if (typeof Swiper !== 'undefined') {
      mountSwiper();
    } else {
      document.addEventListener('swiper:loaded', mountSwiper, { once: true });
      // Fallback for standard script tags
      window.addEventListener('load', mountSwiper, { once: true });
    }
  }

  // Clean up on section remove (Shopify Editor)
  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}

// ------------------------------------------------------- TESTIMONIAL SLIDER -------------------------------------------------------------

  if (!customElements.get('testimonial-slider')) {
    customElements.define('testimonial-slider', class TestimonialSlider extends HTMLElement {
      connectedCallback() {
        if (typeof Swiper === 'undefined') {
          console.warn('Swiper is not defined. Please ensure swiper-bundle.js is loaded.');
          return;
        }
        this.init();
      }

      init() {
        const isRtl = this.getAttribute('data-rtl') === 'true';
        const mediaSwiperEl = this.querySelector('.testimonial-media-column-swiper');
        const contentSwiperEl = this.querySelector('.testimonial-swiper');
        
        let mediaSwiper;

        // Initialize Media Swiper if exists
        if (mediaSwiperEl) {
          mediaSwiper = new Swiper(mediaSwiperEl, {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: true,
            effect: 'fade', // Optional: smooth transition for images
            allowTouchMove: false // Controlled by content slider
          });
        }

        // Initialize Content Swiper
        const contentSwiper = new Swiper(contentSwiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          loop: true,
          rtl: isRtl,
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
          },
          navigation: {
            nextEl: this.querySelector('.testimonial-swiper-button-next'),
            prevEl: this.querySelector('.testimonial-swiper-button-prev'),
          }
        });

        // Two-way controller sync
        if (mediaSwiper) {
          contentSwiper.controller.control = mediaSwiper;
          mediaSwiper.controller.control = contentSwiper;
        }
      }
    });
  }



// ============================================ INSTAGRAM CAROUSEL =================================================

  if (!customElements.get('instagram-carousel')) {
    class InstagramCarousel extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.handleResize = this.init.bind(this);
      }

      connectedCallback() {
        setTimeout(() => this.init(), 10);

        document.addEventListener('shopify:section:load', (e) => {
          if (e.target.contains(this)) this.init();
        });

        window.addEventListener('resize', this.handleResize);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        this.destroySwiper();
      }

      init() {
        this.destroySwiper();
        this.initSwiper();
      }

      initSwiper() {
        const container = this.querySelector('.swiper');
        const scrollbar = this.querySelector('.swiper-scrollbar');

        if (!container) return;

        const slidesPerViewMobile  = parseFloat(this.getAttribute('data-slides-per-view-mobile'))  || 1.2;
        const slidesPerViewDesktop = parseFloat(this.getAttribute('data-slides-per-view-desktop')) || 5;
        const spaceBetween         = parseInt(this.getAttribute('data-space-between'))             || 12;

        this.swiper = new Swiper(container, {
          slidesPerView: slidesPerViewMobile,
          spaceBetween,
          grabCursor: true,
          scrollbar: {
            el: scrollbar,
            draggable: true,
            hide: false,
          },
          breakpoints: {
            750: {
              slidesPerView: 3,
              spaceBetween: 16,
            },
            1200: {
              slidesPerView: slidesPerViewDesktop,
              spaceBetween: 16,
            },
          },
        });
      }

      destroySwiper() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }
    }

    customElements.define('instagram-carousel', InstagramCarousel);
  }


  // ==================================== BANNER CAROUSEL ===============================================

if (!customElements.get('banner-carousel')) {
  customElements.define('banner-carousel', class BannerCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Check if Swiper exists, otherwise wait or load it
      if (typeof Swiper !== 'undefined') {
        this.init();
      } else {
        document.addEventListener('swiper:loaded', () => this.init());
      }
    }

    init() {
      this.slider = new Swiper(this, {
        slicePerView: 1,
        loop: true,
        effect: 'fade',
        touchStartPreventDefault: false,
        touchMoveStopPropagation: false,
        freeMode: true,
        freeModeMomentum: true,
        freeModeMomentumRatio: 1,
        freeModeMomentumVelocityRatio: 1,
        freeModeSticky: false,
        freeModeMinimumVelocity: 0.02,
        nested: true,
        setWrapperSize: true,
        allowTouchMove: function (swiper, event) {
          const targetElement = event.target;
          const isTargetScrollableContainer = targetElement.closest('.collection-tabs-container');
          const isTargetScrollableNav = targetElement.closest('nav.collection-tabs-navigation');

          if (isTargetScrollableContainer || isTargetScrollableNav) {
            return false;
          }
          return true;
        },
        fadeEffect: { crossFade: true },
        pagination: { el: '.swiper-pagination', clickable: true },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        autoplay: false
      });
    }
  });
}


// ================================== Urgency Countdown =====================================

  class UrgencyTimer extends HTMLElement {
    constructor() {
      super();
      this.timerContainer = null;
      this.interval = null;
      this.targetTime = null;
    }

    connectedCallback() {
      this.timerContainer = this.querySelector('#urgency_timer');

      const futureDateAttr = this.getAttribute('data-future-date');

      if (!futureDateAttr) return;

      const targetDate = new Date(futureDateAttr);

      if (isNaN(targetDate.getTime())) return;

      this.targetTime = targetDate.getTime();

      this.startTimer();
    }

    startTimer() {
      this.updateTimer();

      this.interval = setInterval(() => {
        const now = Date.now();
        const distance = this.targetTime - now;

        if (distance <= 0) {
          this.style.display = 'none';
          clearInterval(this.interval);
        } else {
          this.classList.remove('hide');
          this.updateTimer();
        }
      }, 1000);
    }

    updateTimer() {
      const now = Date.now();
      const distance = this.targetTime - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const display = {
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0'),
      };

      if (this.timerContainer) {
        this.timerContainer.innerHTML = `
    <div class="time-block time-block-days">
      <span class="time-value">${display.days}</span>
      <span class="time-unit">${display.days > 1 ? 'D' : 'Day'}</span>
    </div>
    <span class="seperator">:</span>
    <div class="time-block time-block-hours">
      <span class="time-value">${display.hours}</span>
      <span class="time-unit">${display.hours > 1 ? 'H' : 'Hour'}</span>
    </div>
     <span class="seperator">:</span>
    <div class="time-block time-block-minute">
      <span class="time-value">${display.minutes}</span>
      <span class="time-unit">${display.minutes > 1 ? 'M' : 'Min'}</span>
    </div>
     <span class="seperator time-block-second">:</span>
    <div class="time-block time-block-second">
      <span class="time-value">${display.seconds}</span>
      <span class="time-unit">${display.seconds > 1 ? 'S' : 'Sec'}</span>
    </div>
  `;
      }
    }
  }

  customElements.define('custom-deliver-order', UrgencyTimer);


//   ============================ PDP TABS ============================================

// document.addEventListener('click', (e) => {
//   const trigger = e.target.closest('.pdp-tabs__trigger');
//   if (!trigger) return;
//   activateTab(trigger);
// });

// document.addEventListener('keydown', (e) => {
//   if (e.key !== 'Enter' && e.key !== ' ') return;
//   const trigger = e.target.closest('.pdp-tabs__trigger');
//   if (!trigger) return;
//   e.preventDefault();
//   activateTab(trigger);
// });

// function activateTab(trigger) {
//   const tabs = trigger.closest('.pdp-tabs');
//   const index = trigger.dataset.tab;

//   tabs.querySelectorAll('.pdp-tabs__trigger').forEach(t => {
//     t.classList.remove('is-active');
//     t.setAttribute('aria-selected', 'false');
//     t.setAttribute('tabindex', '-1');
//   });
//   trigger.classList.add('is-active');
//   trigger.setAttribute('aria-selected', 'true');
//   trigger.setAttribute('tabindex', '0');

//   tabs.querySelectorAll('.pdp-tabs__panel').forEach(p => {
//     p.classList.remove('is-active');
//     p.hidden = true;
//   });
//   const activePanel = tabs.querySelector(`.pdp-tabs__panel[data-tab="${index}"]`);
//   if (activePanel) {
//     activePanel.classList.add('is-active');
//     activePanel.hidden = false;
//   }
// }


/**
 * Product Overview Tabs Logic
 * Handles click and keyboard navigation for accessibility
 */
const initProductOverviewTabs = () => {
  const tabContainers = document.querySelectorAll('.pdp-overview-tabs');

  tabContainers.forEach(container => {
    const triggers = container.querySelectorAll('.pdp-overview-tabs__trigger');
    const panels = container.querySelectorAll('.pdp-overview-tabs__panel');

    const activateTab = (targetTrigger) => {
      const tabId = targetTrigger.getAttribute('data-tab');

      // 1. Update Triggers
      triggers.forEach(trigger => {
        const isActive = trigger === targetTrigger;
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-selected', isActive);
        trigger.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // 2. Update Panels
      panels.forEach(panel => {
        const isTarget = panel.getAttribute('data-tab-panel') === tabId;
        panel.classList.toggle('is-active', isTarget);
        panel.hidden = !isTarget;
      });
    };

    // Event Delegation for performance
    container.addEventListener('click', (e) => {
      const trigger = e.target.closest('.pdp-overview-tabs__trigger');
      if (trigger) activateTab(trigger);
    });

    // Keyboard support (Enter & Space)
    container.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const trigger = e.target.closest('.pdp-overview-tabs__trigger');
        if (trigger) {
          e.preventDefault();
          activateTab(trigger);
        }
      }
    });
  });
};

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', initProductOverviewTabs);

// Re-init for Shopify Design Mode (Theme Editor)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', initProductOverviewTabs);
}

// ===================================================== Localization SElector ==================================================
  if (!customElements.get('localization-selector')) {
    class LocalizationSelector extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="country_code"]');
        this.items = this.querySelectorAll('dropdown-menu-item');

        this.items.forEach((item) => {
          item.addEventListener('click', this.onItemClick.bind(this));
          item.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.onItemClick(e);
            }
          });
        });
      }

      onItemClick(event) {
        event.preventDefault();
        const item = event.currentTarget;
        const countryCode = item.dataset.value;

        if (this.input) {
          this.input.value = countryCode;
          if (this.form) this.form.submit();
        }
      }
    }
    customElements.define('localization-selector', LocalizationSelector);
  }

//   ======================================= ARTICLE SLIDER ===================================================

if (!customElements.get('article-product-slider')) {
  customElements.define('article-product-slider', class ArticleProductSlider extends HTMLElement {
    connectedCallback() {
      if (typeof Swiper === 'undefined') return;
      this.init();
    }

    init() {
      const desktop = parseFloat(this.getAttribute('data-desktop')) || 3;
      const tablet = parseFloat(this.getAttribute('data-tablet')) || 2;
      const mobile = parseFloat(this.getAttribute('data-mobile')) || 1.2;
      const pagination = this.querySelector('.swiper-pagination');

      new Swiper(this.querySelector('.article-product-swiper'), {
        slidesPerView: mobile,
        spaceBetween: 16,
        scrollbar: {
          el: this.querySelector('.swiper-scrollbar'),
          draggable: true,
          dragSize: 'auto'
        },
        breakpoints: {
          768: {
            slidesPerView: tablet,
          },
          1024: {
            slidesPerView: desktop,
            spaceBetween: 48
          }
        },
        pagination: {
          el: pagination
        }
      });
    }
  });
}

// ======================================BESTSELLUNG CAROUSEL ===========================================

if (!customElements.get('bestselling-carousel')) {
  class BestsellingCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
    }

    connectedCallback() {
      setTimeout(() => this.init(), 10);
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this)) this.init();
      });
    }

    init() {
      this.destroySwiper();
      this.initSwiper();
    }

    initSwiper() {
      const container = this.querySelector('.swiper');
      const nextEl = this.querySelector('.swiper-button-next-custom');
      const prevEl = this.querySelector('.swiper-button-prev-custom');

      if (!container) return;

      const slidesPerViewMobile = parseFloat(this.getAttribute('data-slides-per-view-mobile')) || 1.2;
      const slidesPerViewDesktop = parseFloat(this.getAttribute('data-slides-per-view-desktop')) || 2;
      const spaceBetween = parseInt(this.getAttribute('data-space-between')) || 16;

      this.swiper = new Swiper(container, {
        slidesPerView: slidesPerViewMobile,
        spaceBetween: spaceBetween,
        grabCursor: true,
        navigation: {
          nextEl: nextEl,
          prevEl: prevEl,
        },
        breakpoints: {
          750: {
            slidesPerView: slidesPerViewDesktop,
            spaceBetween: 32,
          }
        },
      });
    }

    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('bestselling-carousel', BestsellingCarousel);
}


// ================================================ GLOBAL Wishlist ======================================================

class WishlistManager extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'shopify_wishlist';
    this.expireTime = 48 * 60 * 60 * 1000; // 48 Hours
    this.container = this.querySelector('#wishlist-items-container');
  }

  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (e.target.hasAttribute('data-wishlist-close')) this.close();
    });

    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      const triggerBtn = e.target.closest('[data-trigger-wishlist]');

      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation(); // Event bubbling protection
        this.toggleItem(toggleBtn.dataset.wishlistToggle);
      }

      if (triggerBtn) {
        e.preventDefault();
        this.open();
      }
    });

    this.updateIcons();
  }

  open() {
    this.classList.add('is-open');
    this.render();
  }

  close() {
    this.classList.remove('is-open');
  }

  getDetails() {
    const data = JSON.parse(localStorage.getItem(this.storageKey));
    if (!data) return [];
    if (new Date().getTime() - data.timestamp > this.expireTime) {
      localStorage.removeItem(this.storageKey);
      return [];
    }
    return data.items || [];
  }

  toggleItem(handle) {
    let items = this.getDetails();
    const index = items.indexOf(handle);
    index > -1 ? items.splice(index, 1) : items.push(handle);
    
    localStorage.setItem(this.storageKey, JSON.stringify({
      timestamp: new Date().getTime(),
      items: items
    }));

    this.updateIcons();
    if (this.classList.contains('is-open')) this.render();
  }

  updateIcons() {
    const items = this.getDetails();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      btn.classList.toggle('is-active', items.includes(btn.dataset.wishlistToggle));
    });
  }

  async render() {
    const items = this.getDetails();
    if (items.length === 0) {
      this.container.innerHTML = '<p class="wishlist-empty-msg">No items in the wish list.</p>';
      return;
    }

    this.container.innerHTML = '<p style="text-align:center; padding:20px;">Updating list...</p>';
    
    const htmls = await Promise.all(items.map(h => 
      fetch(`/products/${h}?view=wishlist-item`).then(r => r.ok ? r.text() : '')
    ));
    this.container.innerHTML = htmls.join('');
  }
}

if (!customElements.get('wishlist-manager')) {
  customElements.define('wishlist-manager', WishlistManager);
}

// ============================= Overview Products CArousel =======================================

if (!customElements.get('product-overview-carousel')) {
  class ProductRecommendationsCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
    }

    connectedCallback() {
      // Small timeout to ensure DOM is ready
      setTimeout(() => this.init(), 50);
      
      // Shopify Editor support
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this)) this.init();
      });
    }

    init() {
      this.destroySwiper();
      this.initSwiper();
    }

    initSwiper() {
      const container = this.querySelector('.swiper');
      if (!container) return;

      const spvMobile = parseFloat(this.getAttribute('data-slides-per-view-mobile')) || 2;
      const spvDesktop = parseFloat(this.getAttribute('data-slides-per-view-desktop')) || 1;
      const spaceBetween = parseInt(this.getAttribute('data-space-between')) || 16;

      this.swiper = new Swiper(container, {
        slidesPerView: spvMobile,
        spaceBetween: spaceBetween,
        grabCursor: true,
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
          hide: false,
        },
        breakpoints: {
          1200: {
            slidesPerView: spvDesktop,
            spaceBetween: 0,
          }
        },
      });
    }

    destroySwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('product-overview-carousel', ProductRecommendationsCarousel);
}

// PDP GAlleERY


class ProductGallerySlider extends HTMLElement {
  connectedCallback() {
    if (typeof Swiper === 'undefined') return;

    const gallery = this.closest('.pgs-gallery');
    const thumbsEl = gallery ? gallery.querySelector('.pgs-thumbs') : null;
    if (!gallery || !thumbsEl) return;
    
    const thumbsSwiper = new Swiper(thumbsEl, {
      slidesPerView: 4,
      spaceBetween: 10,
      scrollbar: {
        el: gallery.querySelector('.swiper-scrollbar'),
        draggable: true,
      },
      navigation: {
        nextEl: gallery.querySelector('.thumb-next'),
        prevEl: gallery.querySelector('.thumb-prev'),
      },
    });

    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      grabCursor: true,
      loop: true, // As mentioned in your liquid comments
      thumbs: { swiper: thumbsSwiper },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true,
      },
    });

    // FIX: Notify the page/VariantSelector that this gallery is built and ready
    this.dispatchEvent(new CustomEvent('gallery:ready', { bubbles: true }));
  }
}

if (!customElements.get('product-gallery-slider')) {
  customElements.define('product-gallery-slider', ProductGallerySlider);
}